/**
 * BlockRenderer Component
 * 
 * Renders the appropriate block component based on block type.
 * Wraps blocks with BlockWrapper in builder mode for selection and drag-drop.
 */

import React, { memo, useMemo } from 'react';
import { AnyMenuBlock, BlockType } from '@/types/menuBuilder';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { cn } from '@/lib/utils';
import { BlockWrapper } from './BlockWrapper';
import { HeaderBlock } from './HeaderBlock';
import { CategoryBlock } from './CategoryBlock';
import { FeaturedBlock } from './FeaturedBlock';
import { BannerBlock } from './BannerBlock';
import { TextBlock } from './TextBlock';
import { DividerBlock } from './DividerBlock';
import { SpacerBlock } from './SpacerBlock';
import { SearchBlock } from './SearchBlock';
import { CategoryNavigationBlock } from './CategoryNavigationBlock';
import { Branch } from '@/types/index';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';

interface BlockRendererProps {
  block: AnyMenuBlock;
  isPreview?: boolean;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  branch?: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  preloadedData?: PublicMenuData;
}

// Map block types to their components
// Using React.ComponentType<any> is necessary here because the block components
// have different prop interfaces, but we dynamically select them based on type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BLOCK_COMPONENTS: Record<BlockType, React.ComponentType<any>> = {
  header: HeaderBlock,
  category: CategoryBlock,
  featured: FeaturedBlock,
  banner: BannerBlock,
  text: TextBlock,
  divider: DividerBlock,
  spacer: SpacerBlock,
  search: SearchBlock,
  'category-navigation': CategoryNavigationBlock,
};

export const BlockRenderer = memo(function BlockRenderer({
  block,
  isPreview = false,
  onSearch,
  searchQuery,
  branch,
  enableRatings = false,
  enableOrdering = false,
  preloadedData,
}: BlockRendererProps) {
  const globalTheme = useMenuBuilderStore((s) => s.globalThemeOverrides);
  
  // Resolve branch
  const { selectedBranchId } = useBranch(true);
  const { branches } = useFilteredBranches();
  
  const effectiveBranch = useMemo(() => {
    if (branch) return branch;
    return branches.find(b => b.id === selectedBranchId) || undefined;
  }, [branch, branches, selectedBranchId]);

  // Determine animation class
  const animationClass = React.useMemo(() => {
    switch (globalTheme?.blockAnimation) {
      case 'fade-in':
        return 'animate-in fade-in duration-700 fill-mode-both';
      case 'slide-up':
        return 'animate-in slide-in-from-bottom-8 fade-in duration-700 fill-mode-both';
      case 'scale-up':
        return 'animate-in zoom-in-95 fade-in duration-500 fill-mode-both';
      default:
        return '';
    }
  }, [globalTheme?.blockAnimation]);

  const Component = BLOCK_COMPONENTS[block.type];

  if (!Component) {
    console.warn(`Unknown block type: ${block.type}`);
    return null;
  }

  const blockContent = (
    <div className={cn("w-full", animationClass)}>
      <Component
        props={block.props}
        isPreview={isPreview}
        onSearch={block.type === 'search' ? onSearch : undefined}
        searchQuery={block.type === 'search' ? searchQuery : undefined}
        branch={effectiveBranch}
        enableRatings={enableRatings}
        enableOrdering={enableOrdering}
        preloadedData={preloadedData}
      />
    </div>
  );

  // In preview mode, render without wrapper
  if (isPreview) {
    return blockContent;
  }

  // In builder mode, wrap with BlockWrapper for interactions
  return <BlockWrapper block={block}>{blockContent}</BlockWrapper>;
});

export default BlockRenderer;
